import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { adminService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AdminJudges = () => {
  const [judges, setJudges] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addJudgeDialogOpen, setAddJudgeDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'add' or 'remove'
  const { user } = useAuth();

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        if (!isMounted) return;
        setLoading(true);
        
        // Load judges first
        await fetchJudges();
        if (!isMounted) return;
        
        // Small delay to prevent race condition
        await new Promise(resolve => setTimeout(resolve, 100));
        if (!isMounted) return;
        
        // Then load users
        await fetchAllUsers();
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading data:', err);
        setError('Failed to load data');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchJudges = async () => {
    try {
      const response = await adminService.getJudges();
      const judgesData = response.data?.data?.judges || response.data?.judges || [];
      setJudges(judgesData);
    } catch (err) {
      console.error('Error fetching judges:', err);
      setError('Failed to load judges');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await adminService.getUsers({ limit: 100 });
      const usersData = response.data?.data?.users || response.data?.users || [];
      setAllUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    }
  };

  const handleMakeJudge = async (userId) => {
    try {
      await adminService.makeUserJudge(userId);
      setSuccess('User successfully made a judge');
      setError('');
      
      // Optimistic update instead of refetching
      const userToPromote = allUsers.find(u => u.user_id === userId);
      if (userToPromote) {
        // Add to judges list
        setJudges(prev => [...prev, { ...userToPromote, role: 'judge' }]);
        // Remove from all users or update role
        setAllUsers(prev => prev.map(u => 
          u.user_id === userId ? { ...u, role: 'judge' } : u
        ));
      }
      
      setConfirmDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error making user judge:', err);
      setError('Failed to make user a judge');
      setSuccess('');
    }
  };

  const handleRemoveJudge = async (userId) => {
    try {
      await adminService.removeJudgeRole(userId);
      setSuccess('Judge role successfully removed');
      setError('');
      
      // Optimistic update instead of refetching
      const judgeToRemove = judges.find(j => j.user_id === userId);
      if (judgeToRemove) {
        // Remove from judges list
        setJudges(prev => prev.filter(j => j.user_id !== userId));
        // Update role in all users
        setAllUsers(prev => prev.map(u => 
          u.user_id === userId ? { ...u, role: 'user' } : u
        ));
      }
      
      setConfirmDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error removing judge role:', err);
      setError('Failed to remove judge role');
      setSuccess('');
    }
  };

  const openConfirmDialog = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (actionType === 'add') {
      handleMakeJudge(selectedUser.user_id);
    } else if (actionType === 'remove') {
      handleRemoveJudge(selectedUser.user_id);
    }
  };

  const getNonJudgeUsers = () => {
    return allUsers.filter(user => 
      user.role === 'user' && 
      !judges.some(judge => judge.user_id === user.user_id)
    );
  };

  if (!user?.role || user.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page. Only admins can manage judges.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading judges...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Judge Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddJudgeDialogOpen(true)}
          disabled={getNonJudgeUsers().length === 0}
        >
          Add Judge
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Current Judges */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GavelIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Current Judges ({judges.length})
                </Typography>
              </Box>
              
              {judges.length === 0 ? (
                <Alert severity="info">
                  No judges have been assigned yet.
                </Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {judges.map((judge) => (
                        <TableRow key={judge.user_id}>
                          <TableCell>
                            {judge.given_name || judge.family_name 
                              ? `${judge.given_name} ${judge.family_name}`.trim()
                              : judge.email.split('@')[0]
                            }
                          </TableCell>
                          <TableCell>{judge.email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={judge.role} 
                              color={judge.role === 'admin' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(judge.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="center">
                            {judge.role !== 'admin' && (
                              <Tooltip title="Remove Judge Role">
                                <IconButton
                                  color="error"
                                  onClick={() => openConfirmDialog(judge, 'remove')}
                                  size="small"
                                >
                                  <PersonRemoveIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Judge Dialog */}
      <Dialog 
        open={addJudgeDialogOpen} 
        onClose={() => setAddJudgeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Judge</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a user to make them a judge. Judges can review and score stories in events.
          </Typography>
          
          {getNonJudgeUsers().length === 0 ? (
            <Alert severity="info">
              No eligible users found. All active users are already judges or admins.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getNonJudgeUsers().map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        {user.given_name || user.family_name 
                          ? `${user.given_name} ${user.family_name}`.trim()
                          : user.email.split('@')[0]
                        }
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setAddJudgeDialogOpen(false);
                            openConfirmDialog(user, 'add');
                          }}
                        >
                          Make Judge
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddJudgeDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          {actionType === 'add' ? 'Make User a Judge?' : 'Remove Judge Role?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {actionType === 'add' 
              ? `Are you sure you want to make ${selectedUser?.email} a judge? They will be able to review and score stories in events.`
              : `Are you sure you want to remove the judge role from ${selectedUser?.email}? They will no longer be able to judge events.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAction}
            variant="contained"
            color={actionType === 'add' ? 'primary' : 'error'}
          >
            {actionType === 'add' ? 'Make Judge' : 'Remove Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminJudges;
